import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactSelect, { components } from "react-select";
import Loader from "../Loader";

const safeArray = x => (Array.isArray(x) ? x : []);

const normalizeIncoming = v => {
    if (v == null) return "";
    if (typeof v === "object") return v.id ?? v.name ?? String(v);
    return v;
};

const equalsNormalized = (optVal, incoming) => String(optVal ?? "") === String(incoming ?? "");

const LineEditMenuList = props => {
    const ref = useRef();
    useEffect(() => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);
    return (
        <components.MenuList {...props} innerRef={ref}>
            {props?.isLoading && props?.isAsyncSearch ? (
                <div className="col-md-12 p-4">
                    <Loader />
                </div>
            ) : (
                <>
                    {props?.isAsyncSearch && props?.count != null && <div className="option-count">Count: {props?.count}</div>}
                    {props.children}
                </>
            )}
        </components.MenuList>
    );
};

const BOOLEAN_OPTIONS = [
    { label: "Yes", value: true },
    { label: "No", value: false }
];

export default function LineEditDropdown({
    optionsList,
    value,
    displayLabel,
    isBoolean = false,
    isGrouped = false,
    isDynamicField = false,
    isLoading = false,
    hasSearchFunction = false,
    count = null,
    placeholder = "Select",
    hasEmptySelect = true,
    onSelect,
    onSearch,
    onClose
}) {
    const builtOptions = useMemo(() => {
        if (isBoolean) {
            return hasEmptySelect ? [{ label: placeholder, value: "" }, ...BOOLEAN_OPTIONS] : BOOLEAN_OPTIONS;
        }

        if (isGrouped) {
            return safeArray(optionsList).map(group => ({
                label: group?.label ?? "",
                options: safeArray(group?.options).map(opt => ({
                    value: opt?.value ?? opt?.id,
                    label: opt?.label ?? opt?.name ?? String(opt?.value ?? opt?.id ?? ""),
                    isDisabled: !!opt?.disabled
                }))
            }));
        }

        const built = safeArray(optionsList)
            .map(opt => {
            const val = isDynamicField ? (opt?.value ?? opt?.id) : opt?.id;
            const lbl = isDynamicField ? (opt?.value ?? opt?.name ?? "") : (opt?.name ?? String(val ?? ""));
            return { value: val, label: String(lbl ?? "") };
            })
            .filter(opt => (hasEmptySelect ? true : !(opt?.value == null || opt?.value === "")));
        return hasEmptySelect ? [{ label: placeholder, value: "" }, ...built] : built;
    }, [optionsList, isBoolean, isGrouped, isDynamicField, hasEmptySelect, placeholder]);

    const flatOptions = useMemo(
        () => (isGrouped ? builtOptions.flatMap(g => safeArray(g?.options)) : builtOptions),
        [builtOptions, isGrouped]
    );

    const normalizedValue = useMemo(() => normalizeIncoming(value), [value]);

    const selectedValue = useMemo(() => {
        const match = flatOptions.find(
            o => equalsNormalized(o?.value, normalizedValue) || (typeof o?.label === "string" && o.label.trim() === String(normalizedValue))
        );
        if (match) return match;
        const fallbackLabel = displayLabel != null && displayLabel !== "" ? String(displayLabel) : String(normalizedValue);
        return { value: String(normalizedValue), label: fallbackLabel };
    }, [flatOptions, normalizedValue, displayLabel]);

    const rsComponents = useMemo(
        () => ({
            MenuList: props => <LineEditMenuList {...props} isLoading={isLoading} isAsyncSearch={hasSearchFunction} count={count} />
        }),
        [isLoading, hasSearchFunction, count]
    );

    const rsStyles = useMemo(
        () => ({
            menuPortal: provided => ({ ...provided, zIndex: 9999 })
        }),
        []
    );

    const handleChange = useCallback(
        picked => {
            if (!picked) {
                onSelect?.("", "");
                return;
            }
            if (picked.value === selectedValue?.value) return;
            onSelect?.(picked.value, picked.label);
        },
        [onSelect, selectedValue]
    );

    const [searchInput, setSearchInput] = useState("");
    const searchTimerRef = useRef(null);

    const handleInputChange = useCallback(
        (text, meta) => {
            if (!hasSearchFunction) return text;
            if (meta?.action === "input-change") {
                setSearchInput(text);
                if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                searchTimerRef.current = setTimeout(() => {
                    onSearch?.(text);
                }, 300);
                return text;
            }
            if (meta?.action === "menu-close" || meta?.action === "set-value") {
                setSearchInput("");
                return "";
            }
            return text;
        },
        [hasSearchFunction, onSearch]
    );

    useEffect(
        () => () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        },
        []
    );

    const handleBlur = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleKeyDown = useCallback(
        e => {
            if (e.key === "Escape") onClose?.();
        },
        [onClose]
    );

    const closeMenuOnScroll = useCallback(e => {
        if (e?.target && typeof e.target.closest === "function") {
            if (e.target.closest(".react-select__menu, .react-select__menu-list")) {
                return false;
            }
        }
        return true;
    }, []);

    return (
        <div className="line-edit-dd-wrap">
            <ReactSelect
                classNamePrefix="react-select"
                options={builtOptions}
                value={selectedValue}
                onChange={handleChange}
                onInputChange={handleInputChange}
                inputValue={hasSearchFunction ? searchInput : undefined}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onMenuClose={onClose}
                isLoading={isLoading}
                isClearable={false}
                autoFocus
                defaultMenuIsOpen
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="absolute"
                menuPlacement="auto"
                closeMenuOnScroll={closeMenuOnScroll}
                components={rsComponents}
                styles={rsStyles}
                filterOption={hasSearchFunction ? () => true : undefined}
                placeholder={placeholder}
            />
        </div>
    );
}
